// BAO-125 Phase 2: first-party attribution capture.
//
// A marketing visit's provenance (utm_*, click ids, referrer host, landing
// path) lives in the `bh_attr` cookie as two touches: `first` is written once
// and never replaced; `last` is replaced by any later touch that carries a
// utm, a click id, or an external referrer. At family creation the server
// parses the cookie through `parseAttributionCookie`, which enforces a fixed
// schema and drops any touch that does not fit - the cookie is client-owned
// and is never trusted as-is.
//
// Pure module: no DOM, no Next imports. Both the client component
// (`components/attribution-capture.tsx`) and the server helper
// (`lib/analytics/family-attribution.ts`) build on it, so the parse/merge
// rules are unit-testable in one place.
import { isMarketingPath } from './marketing-routes'
import { normalizePath } from './sanitize-location'

export const ATTRIBUTION_COOKIE = 'bh_attr'
export const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60
/** Browsers cap a cookie at 4096 bytes; leave headroom for the attribute string. */
export const ATTRIBUTION_COOKIE_MAX_BYTES = 3_800
export const ATTRIBUTION_COOKIE_VERSION = 1

export const CONSENT_COOKIE = 'bh_consent'

export const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
export type UtmField = (typeof UTM_FIELDS)[number]

/** `gbraid` / `wbraid`: Google's iOS click ids, sent instead of `gclid` on many iPhone clicks (#384). */
export const CLICK_ID_FIELDS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const
export type ClickIdField = (typeof CLICK_ID_FIELDS)[number]

/** Free-text fields: utm values, referrer host, landing path. */
export const TOUCH_FIELD_MAX_LENGTH = 100
/**
 * Click ids are opaque tokens minted by the ad platform. Google's gclid is
 * routinely 90-120 characters, so the general 100-char cap would silently drop
 * most real ones; they get their own bound and a stricter charset instead.
 */
export const CLICK_ID_MAX_LENGTH = 255

const TOUCH_FIELD_PATTERN = /^[A-Za-z0-9._+\- ]+$/
const CLICK_ID_PATTERN = /^[A-Za-z0-9_-]+$/
const HOST_PATTERN = /^[a-z0-9.-]+$/
const PATH_PATTERN = /^\/[A-Za-z0-9._~\-/]*$/

/** Ad-platform browser ids, captured only while ads consent is granted (Phase 5b). */
export const AD_ID_FIELDS = ['ga_client_id', 'fbp'] as const
export type AdIdField = (typeof AD_ID_FIELDS)[number]

export interface AttributionTouch {
  /** GA4 client id (the `_ga` cookie minus its `GA1.1.` prefix); ads consent only. */
  ga_client_id?: string
  /** The Meta browser id (`_fbp` cookie value); ads consent only. */
  fbp?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbclid?: string
  /** Hostname of an EXTERNAL referrer; never set for same-site navigation. */
  referrer_host?: string
  /** Pathname only - never a query string, never a hash. */
  landing_path: string
  /** ISO-8601 timestamp of the touch. */
  ts: string
}

export interface Attribution {
  v: typeof ATTRIBUTION_COOKIE_VERSION
  first: AttributionTouch
  last: AttributionTouch
}

// ---------------------------------------------------------------------------
// Parsing a touch from the current page
// ---------------------------------------------------------------------------

function cleanField(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  const value = raw.trim()
  if (value === '' || value.length > TOUCH_FIELD_MAX_LENGTH) return undefined
  if (!TOUCH_FIELD_PATTERN.test(value)) return undefined
  return value
}

function cleanClickId(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  const value = raw.trim()
  if (value === '' || value.length > CLICK_ID_MAX_LENGTH) return undefined
  if (!CLICK_ID_PATTERN.test(value)) return undefined
  return value
}

function cleanHost(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  const value = raw.trim().toLowerCase()
  if (value === '' || value.length > TOUCH_FIELD_MAX_LENGTH) return undefined
  if (!HOST_PATTERN.test(value)) return undefined
  return value
}

function cleanPath(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  // Pathname only: anything from the first `?` or `#` on is dropped, so a
  // token-bearing URL (`/?t=<recovery token>`) can never enter the cookie.
  const pathOnly = normalizePath(raw.split(/[?#]/)[0] || '/')
  if (pathOnly.length > TOUCH_FIELD_MAX_LENGTH) return undefined
  if (!PATH_PATTERN.test(pathOnly)) return undefined
  return pathOnly
}

function cleanTimestamp(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length > 40) return undefined
  const ms = Date.parse(raw)
  if (Number.isNaN(ms)) return undefined
  return new Date(ms).toISOString()
}

/**
 * The referrer's hostname when it is a different site, else undefined.
 * `www.` is stripped so `www.google.com` and `google.com` slice together.
 */
export function externalReferrerHost(referrer: string, ownHost: string): string | undefined {
  if (!referrer) return undefined
  let host: string
  try {
    host = new URL(referrer).hostname.toLowerCase()
  } catch {
    return undefined
  }
  const own = ownHost.toLowerCase().replace(/^www\./, '')
  const bare = host.replace(/^www\./, '')
  if (bare === own || bare.endsWith(`.${own}`)) return undefined
  return cleanHost(bare)
}

export interface ParseTouchInput {
  /** The page URL (absolute or path + query). */
  url: string
  /** `document.referrer` or the Referer header. */
  referrer: string
  /** The site's own hostname, so internal navigation never counts as a referrer. */
  ownHost: string
  now?: Date
  /** Browser ad ids to attach; the caller passes them ONLY with ads consent granted. */
  adIds?: { ga_client_id?: string; fbp?: string }
}

/** `_ga` cookie → client id (`GA1.1.123.456` → `123.456`); null when malformed. */
export function gaClientIdFromCookie(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  const m = raw.match(/^GA1\.[0-9]+\.([0-9]+\.[0-9]+)$/)
  return m ? m[1] : undefined
}

/** `_fbp` cookie value, validated (`fb.1.<ms>.<rand>`). */
export function fbpFromCookie(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  return /^fb\.[0-9]\.[0-9]{10,16}\.[0-9]{1,20}$/.test(raw) ? raw : undefined
}

/**
 * Build a touch for the current page. Always returns a touch (a direct visit
 * still records the landing path); `isAttributableTouch` says whether it may
 * replace `last`.
 */
export function parseTouch(input: ParseTouchInput): AttributionTouch {
  let url: URL
  try {
    url = new URL(input.url, `https://${input.ownHost}`)
  } catch {
    url = new URL(`https://${input.ownHost}/`)
  }
  const touch: AttributionTouch = {
    landing_path: cleanPath(url.pathname) ?? '/',
    ts: (input.now ?? new Date()).toISOString(),
  }
  for (const field of UTM_FIELDS) {
    const value = cleanField(url.searchParams.get(field))
    if (value !== undefined) touch[field] = value
  }
  for (const field of CLICK_ID_FIELDS) {
    const value = cleanClickId(url.searchParams.get(field))
    if (value !== undefined) touch[field] = value
  }
  const host = externalReferrerHost(input.referrer, input.ownHost)
  if (host) touch.referrer_host = host
  const ga = input.adIds?.ga_client_id ? gaClientIdFromCookie(`GA1.1.${input.adIds.ga_client_id}`) : undefined
  if (ga !== undefined) touch.ga_client_id = ga
  const fbp = fbpFromCookie(input.adIds?.fbp)
  if (fbp !== undefined) touch.fbp = fbp
  return touch
}

/** A touch that says something about WHERE the visit came from. */
export function isAttributableTouch(touch: AttributionTouch): boolean {
  return (
    UTM_FIELDS.some(f => touch[f] !== undefined) ||
    CLICK_ID_FIELDS.some(f => touch[f] !== undefined) ||
    touch.referrer_host !== undefined
  )
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/**
 * Fold a new touch into the stored attribution. `first` is immutable once set;
 * `last` moves only for an attributable touch, so an internal page load never
 * erases the campaign that brought the visitor.
 */
export function mergeAttribution(existing: Attribution | null, touch: AttributionTouch): Attribution {
  if (!existing) {
    return { v: ATTRIBUTION_COOKIE_VERSION, first: touch, last: touch }
  }
  if (isAttributableTouch(touch)) {
    return { v: ATTRIBUTION_COOKIE_VERSION, first: existing.first, last: touch }
  }
  // An internal pageview never moves `last`, but the ad ids it carries (set
  // by the pixels after the visitor accepted marketing cookies) are worth
  // keeping: fold them onto `last` without touching anything else.
  const adIds: Partial<Pick<AttributionTouch, AdIdField>> = {}
  for (const f of AD_ID_FIELDS) if (touch[f] !== undefined && existing.last[f] !== touch[f]) adIds[f] = touch[f]
  const last = Object.keys(adIds).length > 0 ? { ...existing.last, ...adIds } : existing.last
  return { v: ATTRIBUTION_COOKIE_VERSION, first: existing.first, last }
}

// ---------------------------------------------------------------------------
// Cookie encode / decode
// ---------------------------------------------------------------------------

function validateTouch(raw: unknown): AttributionTouch | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const landing_path = cleanPath(typeof r.landing_path === 'string' ? r.landing_path : undefined)
  const ts = cleanTimestamp(r.ts)
  if (!landing_path || !ts) return null
  const touch: AttributionTouch = { landing_path, ts }
  for (const field of UTM_FIELDS) {
    if (r[field] === undefined) continue
    const value = cleanField(typeof r[field] === 'string' ? (r[field] as string) : undefined)
    // A present-but-invalid field means the touch was not written by us. Drop it whole.
    if (value === undefined) return null
    touch[field] = value
  }
  for (const field of CLICK_ID_FIELDS) {
    if (r[field] === undefined) continue
    const value = cleanClickId(typeof r[field] === 'string' ? (r[field] as string) : undefined)
    if (value === undefined) return null
    touch[field] = value
  }
  if (r.referrer_host !== undefined) {
    const value = cleanHost(typeof r.referrer_host === 'string' ? (r.referrer_host as string) : undefined)
    if (value === undefined) return null
    touch.referrer_host = value
  }
  if (r.ga_client_id !== undefined) {
    const value = typeof r.ga_client_id === 'string' ? gaClientIdFromCookie(`GA1.1.${r.ga_client_id}`) : undefined
    if (value === undefined) return null
    touch.ga_client_id = value
  }
  if (r.fbp !== undefined) {
    const value = fbpFromCookie(typeof r.fbp === 'string' ? (r.fbp as string) : undefined)
    if (value === undefined) return null
    touch.fbp = value
  }
  return touch
}

/**
 * Validate a raw `bh_attr` cookie value against the fixed schema. Returns
 * null for anything that is not exactly what `encodeAttributionCookie`
 * produces; a valid `first` with an invalid `last` keeps `first` and reuses it
 * as `last`. This is the ONLY way cookie data reaches the server.
 */
export function parseAttributionCookie(raw: string | null | undefined): Attribution | null {
  if (typeof raw !== 'string' || raw === '' || raw.length > ATTRIBUTION_COOKIE_MAX_BYTES) return null
  let decoded: unknown
  try {
    decoded = JSON.parse(decodeURIComponent(raw))
  } catch {
    return null
  }
  if (!decoded || typeof decoded !== 'object') return null
  const d = decoded as Record<string, unknown>
  if (d.v !== ATTRIBUTION_COOKIE_VERSION) return null
  const first = validateTouch(d.first)
  if (!first) return null
  const last = validateTouch(d.last) ?? first
  return { v: ATTRIBUTION_COOKIE_VERSION, first, last }
}

/** The client and server share one decoder. */
export const decodeAttributionCookie = parseAttributionCookie

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length
}

/**
 * Serialize for `document.cookie`. If the payload would exceed the size cap
 * (only possible with adversarially long inputs, since every field is
 * bounded), the optional utm_content / utm_term fields go first, then `last`
 * collapses onto `first`. Never returns more than ATTRIBUTION_COOKIE_MAX_BYTES.
 */
export function encodeAttributionCookie(attr: Attribution): string {
  const candidates: Attribution[] = [
    attr,
    {
      ...attr,
      first: withoutOptionalUtm(attr.first),
      last: withoutOptionalUtm(attr.last),
    },
    { ...attr, first: withoutOptionalUtm(attr.first), last: withoutOptionalUtm(attr.first) },
  ]
  for (const candidate of candidates) {
    const encoded = encodeURIComponent(JSON.stringify(candidate))
    if (byteLength(encoded) <= ATTRIBUTION_COOKIE_MAX_BYTES) return encoded
  }
  // Unreachable with bounded fields; keep the type honest.
  const minimal: Attribution = {
    v: ATTRIBUTION_COOKIE_VERSION,
    first: { landing_path: attr.first.landing_path, ts: attr.first.ts },
    last: { landing_path: attr.first.landing_path, ts: attr.first.ts },
  }
  return encodeURIComponent(JSON.stringify(minimal))
}

function withoutOptionalUtm(touch: AttributionTouch): AttributionTouch {
  const { utm_content: _c, utm_term: _t, ...rest } = touch
  return rest
}

/** Read one cookie out of a `document.cookie` / `Cookie:` header string. */
export function readCookieValue(cookieString: string | null | undefined, name: string): string | undefined {
  if (!cookieString) return undefined
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() !== name) continue
    return part.slice(eq + 1).trim()
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Where capture runs
// ---------------------------------------------------------------------------

/**
 * Capture runs on the marketing surface plus `/signup` - the pages a campaign
 * link can land on. Never on `/auth/*`, `/join`, `/storyteller/*`,
 * `/reset-password`, or anything inside the app.
 */
export function shouldCaptureAttribution(pathname: string): boolean {
  const path = normalizePath(pathname || '/')
  return isMarketingPath(path) || path === '/signup'
}

// ---------------------------------------------------------------------------
// Consent (the `bh_consent` cookie is written by Phase 5a; read-only here)
// ---------------------------------------------------------------------------

export type ConsentState = 'granted' | 'denied' | 'unset'

export interface ConsentSnapshot {
  analytics: ConsentState
  ads: ConsentState
}

/** The current `bh_consent` schema version; bumped in `consent.ts` semantics, mirrored here. */
export const CONSENT_RECORD_VERSION = 1

export interface StoredConsentRecord {
  analytics: 'granted' | 'denied'
  ads: 'granted' | 'denied'
  ts: string
  v: number
}

function isConsentChoice(v: unknown): v is 'granted' | 'denied' {
  return v === 'granted' || v === 'denied'
}

/**
 * THE validator for a raw `bh_consent` value: a JSON object with both
 * categories as `granted|denied`, a parseable `ts`, and a numeric `v`.
 * Anything else is not a record. Every reader (`parseConsentCookie` here,
 * `parseConsentRecord` in consent.ts, and the inline gtag bootstrap which
 * mirrors these rules in JS) applies exactly these rules so one browser can
 * never hold two contradictory answers.
 */
export function validateConsentRecord(raw: string | null | undefined): StoredConsentRecord | null {
  if (typeof raw !== 'string' || raw === '' || raw.length > 500) return null
  let decoded: unknown
  try {
    decoded = JSON.parse(decodeURIComponent(raw))
  } catch {
    return null
  }
  if (!decoded || typeof decoded !== 'object') return null
  const d = decoded as Record<string, unknown>
  if (!isConsentChoice(d.analytics) || !isConsentChoice(d.ads)) return null
  if (typeof d.v !== 'number' || !Number.isInteger(d.v) || d.v < 1) return null
  if (typeof d.ts !== 'string' || Number.isNaN(Date.parse(d.ts))) return null
  return { analytics: d.analytics, ads: d.ads, ts: new Date(d.ts).toISOString(), v: d.v }
}

/**
 * Parse `bh_consent` into the Phase 2 snapshot. Only a CURRENT-version record
 * counts; a stale or malformed one is `unset` (no decision, no ad sends).
 */
export function parseConsentCookie(raw: string | null | undefined): ConsentSnapshot {
  const rec = validateConsentRecord(raw)
  if (!rec || rec.v !== CONSENT_RECORD_VERSION) return { analytics: 'unset', ads: 'unset' }
  return { analytics: rec.analytics, ads: rec.ads }
}

// ---------------------------------------------------------------------------
// PostHog person properties
// ---------------------------------------------------------------------------

export interface AttributionPersonProperties {
  /** `$set_once`: the first touch, written once per person. */
  setOnce: Record<string, string>
  /** `$set`: the last touch, overwritten on every identify. */
  set: Record<string, string>
}

const TOUCH_PERSON_FIELDS = [...UTM_FIELDS, 'referrer_host', 'landing_path'] as const

/**
 * `first_utm_source`, ..., `first_landing_path`, `first_touch_at` for
 * `$set_once`; the `last_*` twins for `$set`. Click ids and browser ad ids are
 * deliberately not person properties (they flow with ads consent, server-side).
 */
export function attributionPersonProperties(attr: Attribution): AttributionPersonProperties {
  const setOnce: Record<string, string> = { first_touch_at: attr.first.ts }
  const set: Record<string, string> = { last_touch_at: attr.last.ts }
  for (const field of TOUCH_PERSON_FIELDS) {
    const first = attr.first[field]
    if (first !== undefined) setOnce[`first_${field}`] = first
    const last = attr.last[field]
    if (last !== undefined) set[`last_${field}`] = last
  }
  return { setOnce, set }
}

// ---------------------------------------------------------------------------
// Self-reported source ("How did you hear about us?")
// ---------------------------------------------------------------------------

/**
 * The single source of the "How did you hear about us?" options. The form
 * renders `label` and posts it verbatim (the label is what
 * `families.onboarding_data.referral_source` has always stored and what the
 * Stripe webhook's Slack notice prints); the route maps it back to `value`
 * for PostHog and rejects anything that is not one of these labels.
 */
export const REFERRAL_OPTIONS = [
  { value: 'friend_family', label: 'Friend or family member' },
  { value: 'social', label: 'Social media (Instagram, Facebook, TikTok, etc.)' },
  { value: 'search', label: 'Search engine (Google, Bing, etc.)' },
  { value: 'ai_search', label: 'AI search (ChatGPT, Perplexity, etc.)' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'other', label: 'Other' },
] as const
export type ReferralSource = (typeof REFERRAL_OPTIONS)[number]['value']
export type ReferralLabel = (typeof REFERRAL_OPTIONS)[number]['label']

/** The enum for a submitted label, or null when the label is not one of ours. */
export function referralSourceFromLabel(label: string): ReferralSource | null {
  const match = REFERRAL_OPTIONS.find(o => o.label === label.trim())
  return match ? match.value : null
}

// ---------------------------------------------------------------------------
// Applying analytics consent
// ---------------------------------------------------------------------------

/**
 * Plan §Consent matrix: attribution is omitted when analytics consent is
 * denied - not stored, not on `family_created`, not on the person. Server
 * and client both route the parsed cookie through here.
 */
export function applyAnalyticsConsent(
  attribution: Attribution | null,
  consent: ConsentSnapshot,
): Attribution | null {
  return consent.analytics === 'denied' ? null : attribution
}
